import axios from 'axios'
import { verifyStoredSession } from './sessionCheck'
import { forgetLogoutReason } from './logoutReason'
import { checkSession, endSession, onSessionEnd, startSession } from './session'
import { useSitesStore } from '@/store/sitesStore'
import { otpErrorMessage } from '@/services/authErrors'
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react'

export interface User {
  id: number
  name: string
  email: string
}

interface LoginPayload {
  email: string
  password: string
}

type LoginResult = { success: boolean; error?: string; requiresOtp?: boolean; requiresPasswordChange?: boolean }

interface AuthContextType {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  isOtpStep: boolean
  pendingEmail: string | null
  // The proof of the password step. The OTP and password-change steps send it instead of a user id.
  loginChallenge: string | null
  otpValidityDuration: number | null // seconds

  accountName: string | null

  login: (data: LoginPayload) => Promise<LoginResult>
  verifyOtp: (code: string) => Promise<{ success: boolean; error?: string }>
  resendOtp: () => Promise<{ success: boolean; error?: string }>
  logout: () => void
}

const API_BASE_URL = import.meta.env.VITE_API_URL

const AuthContext = createContext<AuthContextType | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(
    () => localStorage.getItem('authToken') !== null
  )
  const [error, setError] = useState<string | null>(null)
  const [accountName, setAccountName] = useState<string | null>(null)

  const getLoggedInUser = (data: any, fallbackEmail?: string): User | null => {
    const id = data?.userId ?? data?.user?.id ?? null
    const email = data?.email ?? data?.user?.email ?? fallbackEmail ?? ''
    const name = data?.name ?? data?.userName ?? data?.username ?? data?.user?.name ?? email
    if (!id && !name && !email) return null
    return { id: Number(id ?? 0), name: String(name), email: String(email) }
  }

  const [loginChallenge, setLoginChallenge] = useState<string | null>(null)
  const [isOtpStep, setIsOtpStep] = useState<boolean>(false)
  const [pendingEmail, setPendingEmail] = useState<string | null>(null)
  const [otpValidityDuration, setOtpValidityDuration] = useState<number | null>(
    null
  )

  useEffect(
    () =>
      onSessionEnd(() => {
        setToken(null)
        setUser(null)
        setAccountName(null)
        setIsAuthenticated(false)
        setIsLoading(false)
        useSitesStore.getState().clearSelectedSite()
      }),
    []
  )

  useEffect(() => {
    let cancelled = false
    const savedToken = localStorage.getItem('authToken')

    if (!savedToken) {
      setIsAuthenticated(false)
      setIsLoading(false)
      return
    }

    ;(async () => {
      const verdict = await verifyStoredSession(savedToken)
      if (cancelled) return

      if (verdict === 'invalid') {
        endSession()
        setIsAuthenticated(false)
        setIsLoading(false)
        return
      }
      if (!checkSession()) return

      const liveToken = localStorage.getItem('authToken') ?? savedToken
      setToken(liveToken)
      const savedName = localStorage.getItem('accountName')
      const savedUserName = localStorage.getItem('loggedInUserName')
      if (savedName) setAccountName(savedName)
      if (savedUserName) {
        setUser({ id: Number(localStorage.getItem('userId') ?? 0), name: savedUserName, email: savedUserName })
      }
      setIsAuthenticated(true)
      setIsLoading(false)
      // Refresh account info in the background
      fetchAndSaveAccountId(liveToken)
    })()

    return () => {
      cancelled = true
    }
  }, [])

  // Helper: refresh the account NAME for the currently-selected site only.
  // The site scope (accountId) is owned by SiteProtectedRoute (URL :siteId) — this
  // must NOT write accountId, otherwise every page snaps back to users.site_id.
  const fetchAndSaveAccountId = async (token: string) => {
    const accountId = localStorage.getItem('accountId')
    if (!accountId) return // no site chosen yet (e.g. multi-site user pre-selection)
    try {
      const res = await axios.get(`${API_BASE_URL}/api/water/account-dashboard/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const name = res.data?.data?.name
      if (name) {
        localStorage.setItem('accountName', name)
        setAccountName(name)
      }
    } catch (err) {
      console.error('[AuthContext] Failed to fetch account info:', err)
    }
  }

  const login = useCallback(
    async ({ email, password }: LoginPayload): Promise<LoginResult> => {
      setIsLoading(true)
      setError(null)

      try {
        const deviceToken = localStorage.getItem(`deviceToken_${email}`)
        const res = await axios.post(
          `${API_BASE_URL}/api/auth`,
          { userName: email, password },
          {
            headers: deviceToken ? { 'X-Device-Token': deviceToken } : undefined,
          }
        )

        const apiData = res?.data?.data ?? res?.data
        const step: string | null = apiData?.step ?? null
        // Handle nested token object: {token: {token: "...", ttl: "..."}}
        const tokenData = apiData?.token
        const receivedToken: string | null = typeof tokenData === 'string' ? tokenData : tokenData?.token ?? null
        const receivedUserId: number | string | null = apiData?.userId ?? null
        const receivedChallenge: string | null = apiData?.challenge ?? null
        const loggedInUser = getLoggedInUser(apiData, email)
        const sent = apiData?.otpValidityDuration
        const otpValidity = typeof sent === 'number' ? sent : null

        if (step === 'MFA_REQUIRED' || step === 'MFA_REQ' || step === 'MFA_Req') {
          setLoginChallenge(receivedChallenge)
          setPendingEmail(email)
          setIsOtpStep(true)
          setIsAuthenticated(false)
          setOtpValidityDuration(otpValidity)

          setIsLoading(false)
          return { success: true, requiresOtp: true }
        }

        // Handle password expired
        if (step === 'PASSWORD_EXPIRE' || step === 'PASSWORD_EXPIRED') {
          setPendingEmail(email)
          setLoginChallenge(receivedChallenge)
          setIsLoading(false)
          return { success: true, requiresPasswordChange: true }
        }

        // Handle IP rate limited
        if (step === 'IP_RATE_LIMITED') {
          const msg = 'Too many login attempts from this location. Please try again later.'
          setError(msg)
          setIsAuthenticated(false)
          setIsLoading(false)
          return { success: false, error: msg }
        }

        // Handle max login attempts exceeded
        if (step === 'MAX_LOGIN_ATTEMPTS') {
          const msg = 'חרגת ממספר ניסיונות ההתחברות המקסימלי. נא לנסות שוב מאוחר יותר.'
          setError(msg)
          setIsAuthenticated(false)
          setIsLoading(false)
          return { success: false, error: msg }
        }

        if (receivedToken) {
          startSession(receivedToken, tokenData)
          if (receivedUserId) {
            localStorage.setItem('userId', String(receivedUserId))
          }
          setToken(receivedToken)
          if (loggedInUser) {
            setUser(loggedInUser)
            localStorage.setItem('loggedInUserName', loggedInUser.name)
          }

          // Fetch and save accountId after successful auth
          await fetchAndSaveAccountId(receivedToken)

          setIsAuthenticated(true)
          setIsOtpStep(false)
          setLoginChallenge(null)

          setIsLoading(false)
          return { success: true, requiresOtp: false }
        }

        const msg = 'Unexpected login response (missing step/token)'
        setError(msg)
        setIsAuthenticated(false)
        setIsOtpStep(false)
        setLoginChallenge(null)
        setIsLoading(false)
        return { success: false, error: msg }
      } catch (err: unknown) {
        const axiosError = err as any
        const msg =
          axiosError?.response?.data?.error?.message ??
          axiosError?.response?.data?.message ??
          'Login failed'

        setError(msg)
        setIsAuthenticated(false)
        setIsOtpStep(false)
        setLoginChallenge(null)
        setIsLoading(false)
        return { success: false, error: msg }
      }
    },
    []
  )

  const verifyOtp = useCallback(
    async (code: string): Promise<{ success: boolean; error?: string }> => {
      if (!loginChallenge) {
        return { success: false, error: otpErrorMessage(null) }
      }

      setIsLoading(true)
      setError(null)

      try {
        const res = await axios.post(`${API_BASE_URL}/api/auth/verify-otp`, {
          challenge: loginChallenge,
          otp: code,
        })

        const apiData = res?.data?.data ?? res?.data
        const receivedToken: string | null = apiData?.token ?? null

        if (!receivedToken) {
          const msg = 'Unexpected verifyOtp response (missing token)'
          setError(msg)
          setIsAuthenticated(false)
          setIsLoading(false)
          return { success: false, error: msg }
        }

        startSession(receivedToken, apiData)
        setToken(receivedToken)
        const loggedInUser = getLoggedInUser(apiData, pendingEmail ?? undefined)
        if (loggedInUser) {
          setUser(loggedInUser)
          localStorage.setItem('loggedInUserName', loggedInUser.name)
        }

        // Store trusted device token if returned
        const deviceTokenValue = apiData?.deviceToken
        if (deviceTokenValue && pendingEmail) {
          localStorage.setItem(`deviceToken_${pendingEmail}`, deviceTokenValue)
        }

        // Fetch and save accountId after successful auth
        await fetchAndSaveAccountId(receivedToken)

        setIsAuthenticated(true)
        setIsOtpStep(false)
        setLoginChallenge(null)
        setUser(null)

        setIsLoading(false)
        return { success: true }
      } catch (err: unknown) {
        const msg = otpErrorMessage(err)

        setError(msg)
        setIsAuthenticated(false)
        setIsLoading(false)
        return { success: false, error: msg }
      }
    },
    [loginChallenge]
  )

  const resendOtp = useCallback(
    async (): Promise<{ success: boolean; error?: string }> => {
      if (!loginChallenge) {
        return { success: false, error: otpErrorMessage(null) }
      }

      try {
        await axios.post(
          `${API_BASE_URL}/api/auth/resend-otp`,
          { challenge: loginChallenge },
          { headers: { 'Content-Type': 'application/json' } }
        )
        return { success: true }
      } catch (err: unknown) {
        return { success: false, error: otpErrorMessage(err) }
      }
    },
    [loginChallenge]
  )


  const logout = useCallback(() => {
    const storedToken = localStorage.getItem('authToken')
    if (storedToken) {
      // Not awaited: the user is logged out locally whatever the Gateway answers.
      axios
        .post(`${API_BASE_URL}/api/auth/logout`, null, {
          headers: { Authorization: `Bearer ${storedToken}` },
        })
        .catch(() => {})
    }
    endSession()
    forgetLogoutReason()
    setIsAuthenticated(false)
    setIsOtpStep(false)
    setLoginChallenge(null)
    setOtpValidityDuration(null)
    setError(null)
  }, [])

  const value = useMemo(
    () => ({
      user,
      token,
      isAuthenticated,
      isLoading,
      error,
      accountName,
      isOtpStep,
      pendingEmail,
      loginChallenge,
      otpValidityDuration,
      login,
      verifyOtp,
      resendOtp,
      logout,
    }),
    [
      user,
      token,
      isAuthenticated,
      isLoading,
      error,
      accountName,
      isOtpStep,
      pendingEmail,
      loginChallenge,
      otpValidityDuration,
      login,
      verifyOtp,
      resendOtp,
      logout,
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
